import { Alert, Box, Card, CardContent, CircularProgress, Container, Typography } from '@mui/material';
import { ApprovalRoleSchema, type ApprovalRole } from '@tto/shared-models';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Logo from 'src/components/LogoSign';
import {
  type ApprovalError,
  useResolveApprovalLinkMutation,
} from 'src/services/restApi/queries/approval.service';

function decodeTokenPayload(token: string): { role?: ApprovalRole } | null {
  try {
    const base64Payload = token.split('.')[1];
    const decoded = atob(base64Payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

function getTokenFromLocation(hash: string, search: string): string | null {
  const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const hashToken = hashParams.get('token');

  if (hashToken) {
    return hashToken;
  }

  const searchParams = new URLSearchParams(search);
  return searchParams.get('token');
}

export const ApprovePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationToken = useMemo(
    () => getTokenFromLocation(location.hash, location.search),
    [location.hash, location.search],
  );
  const [token, setToken] = useState<string | null>(locationToken);
  const [isIframeLoading, setIsIframeLoading] = useState(false);
  const requestedTokenRef = useRef<string | null>(null);
  const [resolveApprovalLink, { data, error, isLoading, reset }] = useResolveApprovalLinkMutation();

  useEffect(() => {
    if (!locationToken) {
      return;
    }

    requestedTokenRef.current = null;
    reset();
    setToken(locationToken);

    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.delete('token');

    // Tokens in the URL fragment stay in the browser, so we copy the token into memory and
    // immediately scrub it from the address bar before resolving the approval link.
    navigate(
      {
        pathname: location.pathname,
        search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : '',
      },
      { replace: true },
    );
  }, [location.pathname, location.search, locationToken, navigate, reset]);

  const payload = token ? decodeTokenPayload(token) : null;
  const role = payload?.role
    ? ApprovalRoleSchema.safeParse(payload.role).data ?? null
    : null;
  const approvalError =
    error && typeof error === 'object' && error !== null && 'status' in error
      ? (error as ApprovalError)
      : null;

  useEffect(() => {
    if (!token || !role || requestedTokenRef.current === token) {
      return;
    }

    requestedTokenRef.current = token;
    void resolveApprovalLink({ role, token, __silenceToast: true });
  }, [resolveApprovalLink, role, token]);

  useEffect(() => {
    setIsIframeLoading(Boolean(data?.url));
  }, [data?.url]);

  const approvalUrl = data?.url ?? null;
  const hasApprovalUrl = Boolean(approvalUrl);

  const renderStatusContent = () => {
    if (!token) {
      return (
        <Alert severity='error'>
          Missing approval token. Please use the link from your email.
        </Alert>
      );
    }

    if (!role) {
      return (
        <Alert severity='error'>
          Invalid approval link. Please use the link from your email.
        </Alert>
      );
    }

    if (isLoading) {
      return (
        <Box display='flex' flexDirection='column' alignItems='center' gap={2} py={4}>
          <CircularProgress />
          <Typography variant='body1'>Loading approval form...</Typography>
        </Box>
      );
    }

    if (approvalError) {
      const status = approvalError.status;
      if (status === 401) {
        return (
          <Alert severity='warning'>
            This approval link has expired or is invalid. Please contact the TTO office for a new link.
          </Alert>
        );
      }
      if (status === 409) {
        return (
          <Alert severity='info'>
            {approvalError.data?.message ??
              'This disclosure is not currently at the approval stage. Please wait for further communication.'}
          </Alert>
        );
      }
      return (
        <Alert severity='error'>
          Something went wrong. Please try again or contact the TTO office.
        </Alert>
      );
    }

    return null;
  };

  return (
    <Box sx={{ minHeight: '100vh', height: '100vh', bgcolor: 'background.default', display: 'flex' }}>
      <Box
        sx={{
          width: { xs: 88, md: 112 },
          px: { xs: 1.5, md: 2 },
          py: { xs: 2, md: 3 },
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderRight: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <Logo width={72} />
        </Box>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, p: { xs: 1.5, md: 2 } }}>
        {hasApprovalUrl && !isLoading && !approvalError ? (
          <Box
            sx={{
              height: '100%',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'background.paper',
              boxShadow: 1,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                height: '100%',
                bgcolor: 'background.paper',
              }}
            >
              {isIframeLoading ? (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    px: 3,
                    textAlign: 'center',
                    bgcolor: 'background.paper',
                  }}
                >
                  <CircularProgress />
                  <Typography variant='h6'>Loading approval form...</Typography>
                  <Typography variant='body2' color='text.secondary'>
                    The Jotform page will appear as soon as it finishes loading.
                  </Typography>
                </Box>
              ) : null}

              <iframe
                src={approvalUrl ?? undefined}
                title='Approval Form'
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  opacity: isIframeLoading ? 0 : 1,
                  transition: 'opacity 150ms ease-in-out',
                }}
                allow='camera; microphone'
                referrerPolicy='no-referrer'
                onLoad={() => setIsIframeLoading(false)}
              />
            </Box>
          </Box>
        ) : (
          <Container
            maxWidth='sm'
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'center',
              py: { xs: 3, md: 4 },
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Card>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Typography variant='h4' gutterBottom>
                    Approval Portal
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                    We&apos;re preparing your disclosure approval form. This secure link only opens the current approval
                    step for review.
                  </Typography>
                  {renderStatusContent()}
                </CardContent>
              </Card>
            </Box>
          </Container>
        )}
      </Box>
    </Box>
  );
};

export default ApprovePage;
